import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Link,
  Divider,
  Stack,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { GitHub, LinkedIn, Instagram } from '@mui/icons-material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

// Firebase Imports
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

// --- Styled Components for Light Modern Theme ---
const FooterLink = styled(Link)(({ theme }) => ({
  color: '#475569',
  textDecoration: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  '&:hover': {
    color: '#2563eb',
    transform: 'translateX(4px)',
  },
}));

const SocialButton = styled(IconButton)(({ hovercolor }) => ({
  color: '#475569',
  background: '#f1f5f9',
  border: '1px solid #e2e8f0',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: '10px',
  '&:hover': {
    color: '#ffffff',
    background: hovercolor || '#2563eb',
    borderColor: hovercolor || '#2563eb',
    transform: 'translateY(-3px)',
    boxShadow: `0 10px 15px -3px ${hovercolor || '#2563eb'}33`,
  },
}));

const Footer = () => {
  const [contactDetails, setContactDetails] = useState({
    phone: '',
    email: '',
    location: '',
    mapLink: '',
    github: '',
    linkedin: '',
    instagram: '',
  });
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    const fetchFooterInfo = async () => {
      try {
        const docRef = doc(db, 'adminSettings', 'contactInfo');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setContactDetails(docSnap.data());
        }
      } catch (err) {
        console.error('Footer Fetch Error:', err);
      } finally {
        setFetching(false);
      }
    };
    fetchFooterInfo();
  }, []);

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Me', path: '/about' },
    { name: 'Services Overview', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceLinks = [
    { name: 'Android Development', path: '/services/android-development' },
    { name: 'Flutter Development', path: '/services/flutter-development' },
    { name: 'React Native', path: '/services/react-native' },
    { name: 'Hybrid App Dev', path: '/services/hybrid-app-development' },
    { name: 'Web Development', path: '/services/web-development' },
    { name: 'IT Consulting', path: '/services/it-consulting' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#ffffff',
        pt: { xs: 8, md: 10 },
        pb: 4,
        borderTop: '1px solid #f1f5f9',
        color: '#1e293b',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 5 }} columns={12}>
          {/* Brand & Social Column */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography
              variant="h5"
              component={RouterLink}
              to="/"
              sx={{
                fontWeight: 900,
                color: '#0f172a',
                mb: 2,
                letterSpacing: '-0.5px',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              VIKASH <span style={{ color: '#2563eb' }}>TIWARI</span>
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                mb: 3.5,
                lineHeight: 1.7,
                maxWidth: '340px',
                fontSize: '0.9rem',
              }}
            >
              Engineering high-performance mobile applications, enterprise platforms, and scalable digital ecosystems with clean software architecture.
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <SocialButton
                component="a"
                href={contactDetails.github || '#'}
                target="_blank"
                rel="noopener noreferrer"
                hovercolor="#0f172a"
                aria-label="GitHub"
              >
                <GitHub fontSize="small" />
              </SocialButton>

              <SocialButton
                component="a"
                href={contactDetails.linkedin || '#'}
                target="_blank"
                rel="noopener noreferrer"
                hovercolor="#0a66c2"
                aria-label="LinkedIn"
              >
                <LinkedIn fontSize="small" />
              </SocialButton>

              <SocialButton
                component="a"
                href={contactDetails.instagram || '#'}
                target="_blank"
                rel="noopener noreferrer"
                hovercolor="#e1306c"
                aria-label="Instagram"
              >
                <Instagram fontSize="small" />
              </SocialButton>

              <SocialButton
                component="a"
                href={contactDetails.email ? `mailto:${contactDetails.email}` : '#'}
                hovercolor="#2563eb"
                aria-label="Email"
              >
                <EmailOutlinedIcon fontSize="small" />
              </SocialButton>
            </Stack>
          </Grid>

          {/* Quick Navigation Links */}
          <Grid size={{ xs: 6, sm: 6, md: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 2.5,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '0.75rem',
              }}
            >
              Navigation
            </Typography>

            <Stack spacing={1.5}>
              {quickLinks.map((link) => (
                <FooterLink key={link.path} component={RouterLink} to={link.path}>
                  {link.name}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Specialization Services Links */}
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 2.5,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '0.75rem',
              }}
            >
              Services & Expertise
            </Typography>

            <Stack spacing={1.5}>
              {serviceLinks.map((service) => (
                <FooterLink key={service.path} component={RouterLink} to={service.path}>
                  {service.name}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Contact Details Card */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box
              sx={{
                p: 3,
                bgcolor: '#f8fafc',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.02)',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5, fontSize: '0.95rem' }}
              >
                Let's Build Together
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                Open for enterprise & contract roles.
              </Typography>

              <Stack spacing={2}>
                {/* Email */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                  {fetching ? (
                    <Skeleton width="130px" height="20px" />
                  ) : (
                    <Link
                      href={contactDetails.email ? `mailto:${contactDetails.email}` : '#'}
                      sx={{
                        textDecoration: 'none',
                        color: '#334155',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                        '&:hover': { color: '#2563eb' },
                      }}
                    >
                      {contactDetails.email || 'Email unavailable'}
                    </Link>
                  )}
                </Box>

                {/* Phone */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PhoneOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                  {fetching ? (
                    <Skeleton width="100px" height="20px" />
                  ) : (
                    <Link
                      href={contactDetails.phone ? `tel:${contactDetails.phone}` : '#'}
                      sx={{
                        textDecoration: 'none',
                        color: '#334155',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        '&:hover': { color: '#2563eb' },
                      }}
                    >
                      {contactDetails.phone || 'Phone unavailable'}
                    </Link>
                  )}
                </Box>

                {/* Location */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LocationOnOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                  {fetching ? (
                    <Skeleton width="120px" height="20px" />
                  ) : (
                    <Link
                      href={contactDetails.mapLink || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: 'none',
                        color: '#334155',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        '&:hover': { color: '#2563eb' },
                      }}
                    >
                      {contactDetails.location || 'Location unavailable'}
                    </Link>
                  )}
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6, borderColor: '#f1f5f9' }} />

        {/* Footer Sub-Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
            © {new Date().getFullYear()} Vikash Tiwari. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={3}>
            <FooterLink component={RouterLink} to="/contact" sx={{ fontSize: '0.75rem' }}>
              Privacy Policy
            </FooterLink>
            <FooterLink component={RouterLink} to="/contact" sx={{ fontSize: '0.75rem' }}>
              Terms of Service
            </FooterLink>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;

// import React, { useState, useEffect } from 'react';
// import { Link as RouterLink } from 'react-router-dom';
// import { Box, Container, Typography, Grid, IconButton, Link, Divider, Stack, Skeleton } from '@mui/material';
// import { styled } from '@mui/material/styles';
// import {
//     GitHub,
//     LinkedIn,
//     Instagram,
// } from '@mui/icons-material';
// import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
// import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
// import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
// // Firebase Imports
// import { db } from '../firebaseConfig'; // Path check kar lena
// import { doc, getDoc } from 'firebase/firestore';

// // --- Styled Components ---
// const FooterLink = styled(Link)({
//     color: '#4b5563',
//     textDecoration: 'none',
//     fontSize: '0.9rem',
//     fontWeight: 500,
//     transition: 'all 0.2s ease-in-out',
//     display: 'flex',
//     alignItems: 'center',
//     gap: '4px',
//     '&:hover': {
//         color: '#2563eb',
//         transform: 'translateX(4px)',
//     }
// });

// const SocialButton = styled(IconButton)(({ hovercolor }) => ({
//     color: '#4b5563',
//     background: '#f3f4f6',
//     transition: 'all 0.3s ease',
//     marginRight: '12px',
//     padding: '10px',
//     '&:hover': {
//         color: '#fff',
//         background: hovercolor || '#2563eb',
//         transform: 'translateY(-5px)',
//         boxShadow: `0 10px 15px -3px ${hovercolor}44`,
//     }
// }));

// const Footer = () => {

//     // 1. STATE FOR DYNAMIC DATA
//     const [contactDetails, setContactDetails] = useState({
//         phone: '',
//         email: '',
//         location: '',
//         mapLink: '',
//         github: '',
//         linkedin: '',
//         instagram: ''
//     });
//     const [fetching, setFetching] = useState(true);

//     // 2. FETCH DATA FROM FIRESTORE
//     useEffect(() => {
//         const fetchFooterInfo = async () => {
//             try {
//                 const docRef = doc(db, "adminSettings", "contactInfo");
//                 const docSnap = await getDoc(docRef);
//                 if (docSnap.exists()) {
//                     setContactDetails(docSnap.data());
//                     console.log("Footer Data:", docSnap.data());
//                 }
//             } catch (err) {
//                 console.error("Footer Fetch Error:", err);
//             } finally {
//                 setFetching(false);
//             }
//         };
//         fetchFooterInfo();
//     }, []);


//     return (
//         <Box sx={{
//             bgcolor: '#ffffff',
//             pt: 10,
//             pb: 4,
//             borderTop: '1px solid #f3f4f6'
//         }}>
//             <Container maxWidth="lg">
//                 <Grid container spacing={10}>

//                     {/* Brand & Description */}
//                     <Grid item xs={12} md={4}>
//                         <Typography variant="h5" sx={{ fontWeight: 900, color: '#111827', mb: 2, letterSpacing: '-0.8px' }}>
//                             VIKASH <span style={{ color: '#2563eb' }}>TIWARI</span>
//                         </Typography>
//                         <Typography variant="body2" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.8, maxWidth: '320px' }}>
//                             Building digital experiences with precision and passion.
//                             Let's transform your ideas into high-performance reality.
//                         </Typography>
//                         <Stack direction="row" alignItems="center" justifyContent="center" spacing={3}>
//                             {/* GitHub Profile */}
//                             <SocialButton
//                                 component="a"
//                                 href={contactDetails.github || "#"}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 hovercolor="#171515"
//                             >
//                                 <GitHub fontSize="small" />
//                             </SocialButton>

//                             {/* LinkedIn Profile */}
//                             <SocialButton
//                                 component="a"
//                                 href={contactDetails.linkedin || "#"}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 hovercolor="#0077b5"
//                             >
//                                 <LinkedIn fontSize="small" />
//                             </SocialButton>

//                             <SocialButton
//                                 component="a"
//                                 href={contactDetails.instagram || "#"}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 hovercolor="#e4405f"><Instagram fontSize="small" /></SocialButton>

//                             <SocialButton
//                                 component="a"
//                                 href={`mailto:${contactDetails.email}` || "#"}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 hovercolor="#1da1f2"><EmailOutlinedIcon fontSize="small" /></SocialButton>
//                         </Stack>
//                     </Grid>

//                     {/* Quick Navigation */}
//                     <Grid item xs={6} md={2}>
//                         <Typography
//                             variant="subtitle2"
//                             sx={{
//                                 fontWeight: 700,
//                                 color: '#111827',
//                                 mb: 3,
//                                 textTransform: 'uppercase',
//                                 letterSpacing: '1px'
//                             }}
//                         >
//                             Navigation
//                         </Typography>

//                         <Stack spacing={2}>
//                             <FooterLink href="/">Home</FooterLink>
//                             <FooterLink href="/about">About Me</FooterLink>
//                             <FooterLink href="/services">Services</FooterLink>
//                             <FooterLink href="/portfolio">Portfolio</FooterLink>
//                             <FooterLink href="/contact">Contact</FooterLink>
//                         </Stack>
//                     </Grid>

//                     {/* Specializations */}
//                     <Grid item xs={6} md={2}>
//                         <Typography
//                             variant="subtitle2"
//                             sx={{
//                                 fontWeight: 700,
//                                 color: '#111827',
//                                 mb: 3,
//                                 textTransform: 'uppercase',
//                                 letterSpacing: '1px'
//                             }}
//                         >
//                             Expertise
//                         </Typography>

//                         <Stack spacing={2}>
//                             <FooterLink
//                                 component={RouterLink}
//                                 to="/services#android-dev"
//                             >
//                                 Android App Development
//                             </FooterLink>

//                             <FooterLink
//                                 component={RouterLink}
//                                 to="/services#hybrid-dev"
//                             >
//                                 Hybrid App Development
//                             </FooterLink>

//                             <FooterLink
//                                 component={RouterLink}
//                                 to="/services#web-dev"
//                             >
//                                 Website Development
//                             </FooterLink>

//                             <FooterLink
//                                 component={RouterLink}
//                                 to="/services#it-consult"
//                             >
//                                 IT Consultation
//                             </FooterLink>
//                         </Stack>
//                     </Grid>

//                     {/* Contact Info Section */}
//                     <Grid item xs={12} md={4}>
//                         <Box sx={{
//                             p: 3,
//                             bgcolor: '#f9fafb',
//                             borderRadius: '24px',
//                             border: '1px solid #f3f4f6'
//                         }}>
//                             <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
//                                 Have a project in mind?
//                             </Typography>

//                             <Stack spacing={2} sx={{ mt: 2 }}>
//                                 {/* Clickable Email */}
//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                                     <EmailOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
//                                     <Link
//                                         href={`mailto:${contactDetails.email}`}
//                                         sx={{ textDecoration: 'none', color: '#374151', fontWeight: 600, '&:hover': { color: '#2563eb' } }}
//                                     >
//                                         {contactDetails.email}
//                                     </Link>
//                                 </Box>

//                                 {/* Clickable Phone */}
//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                                     <PhoneOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
//                                     <Link
//                                         href={`tel:${contactDetails.phone}`}
//                                         sx={{ textDecoration: 'none', color: '#374151', fontWeight: 600, '&:hover': { color: '#2563eb' } }}
//                                     >
//                                         {contactDetails.phone}
//                                     </Link>
//                                 </Box>

//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                                     <LocationOnOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
//                                     <Link
//                                         href={contactDetails.mapLink}
//                                         target="_blank"
//                                         rel="noopener noreferrer"
//                                         sx={{ textDecoration: 'none', color: '#374151', fontWeight: 600, '&:hover': { color: '#2563eb' } }}
//                                     >
//                                         {contactDetails.location}
//                                     </Link>
//                                 </Box>
//                             </Stack>
//                         </Box>
//                     </Grid>
//                 </Grid>

//                 <Divider sx={{ mt: 8, mb: 4, opacity: 0.6 }} />

//                 <Box sx={{
//                     display: 'flex',
//                     flexDirection: { xs: 'column', md: 'row' },
//                     justifyContent: 'space-between',
//                     alignItems: 'center',
//                     gap: 2
//                 }}>
//                     <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 500 }}>
//                         © {new Date().getFullYear()} Vikash Tiwari. Crafted with ❤️
//                     </Typography>
//                     <Stack direction="row" spacing={4}>
//                         <FooterLink href="#">Privacy</FooterLink>
//                         <FooterLink href="#">Terms</FooterLink>
//                     </Stack>
//                 </Box>
//             </Container>
//         </Box>
//     );
// };

// export default Footer;